/**
 * Role utility functions to centralize role-based logic
 */

/**
 * Check if a user role can be assigned to jobs
 * ADMIN can be assigned if they have area assignments (indicating they do fieldwork)
 * GUEST_APP_MANAGER should NOT be included here as they manage but don't do the work
 */
export const canBeAssignedJobs = (userRole, userAreas = []) => {
  const jobAssignableRoles = [
    'HOUSEKEEPER',
    'HOUSEKEEPER_MANAGER',
    'HOUSEKEEPER_INSPECTOR', 
    'INSPECTOR',
    'PROPERTY_MANAGER',
    'MAINTENANCE'
  ];
  
  // ADMIN can be assigned to jobs if they have area assignments
  if (userRole === 'ADMIN' && userAreas && userAreas.length > 0) {
    return true;
  }
  
  // GUEST_APP_MANAGER excluded - they manage but don't do the work
  return jobAssignableRoles.includes(userRole);
};

/**
 * Check if a user role can be assigned to work orders
 * ADMIN can be assigned if they have area assignments (indicating they do fieldwork)
 * GUEST_APP_MANAGER should NOT be included as they manage but don't do the work
 */
export const canBeAssignedWorkOrders = (userRole, userAreas = []) => {
  const workOrderAssignableRoles = [
    'HOUSEKEEPER',
    'HOUSEKEEPER_MANAGER',
    'HOUSEKEEPER_INSPECTOR', 
    'INSPECTOR',
    'PROPERTY_MANAGER',
    'MAINTENANCE'
  ];
  
  // ADMIN can be assigned to work orders if they have area assignments
  if (userRole === 'ADMIN' && userAreas && userAreas.length > 0) {
    return true;
  }
  
  // GUEST_APP_MANAGER excluded - they manage but don't do the work
  return workOrderAssignableRoles.includes(userRole);
};

/**
 * Check if a user role has administrative permissions
 * GUEST_APP_MANAGER should have admin-level access
 */
export const hasAdminPermissions = (userRole) => {
  return ['ADMIN', 'GUEST_APP_MANAGER'].includes(userRole);
};

/**
 * Check if a user role can manage jobs (create, edit, cancel)
 * GUEST_APP_MANAGER should be able to manage jobs
 */
export const canManageJobs = (userRole) => {
  return ['ADMIN', 'GUEST_APP_MANAGER', 'HOUSEKEEPER_MANAGER', 'PROPERTY_MANAGER'].includes(userRole);
};

/**
 * Check if a user role can manage teams
 * GUEST_APP_MANAGER should be able to manage teams
 */
export const canManageTeams = (userRole) => {
  return ['ADMIN', 'GUEST_APP_MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER'].includes(userRole);
};

/**
 * Check if a user role can view financial information
 * GUEST_APP_MANAGER should have access to financial info like ADMIN
 */
export const canViewFinancials = (userRole) => {
  return ['ADMIN', 'GUEST_APP_MANAGER', 'HOUSEKEEPER_MANAGER'].includes(userRole);
};

/**
 * Check if a user role can manage properties
 * GUEST_APP_MANAGER should be able to manage properties
 */
export const canManageProperties = (userRole) => {
  return ['ADMIN', 'GUEST_APP_MANAGER', 'PROPERTY_MANAGER'].includes(userRole);
};

/**
 * Check if a user role can cancel/restore jobs
 * GUEST_APP_MANAGER should be able to cancel/restore jobs like ADMIN
 */
export const canCancelRestoreJobs = (userRole) => {
  return ['ADMIN', 'GUEST_APP_MANAGER', 'HOUSEKEEPER_MANAGER'].includes(userRole);
};

/**
 * Get users that can be assigned to jobs, filtered by property area if applicable
 */
export const getAssignableUsers = (users, property = null) => {
  return users.filter(user => {
    // Must be active and assignable to jobs
    if (!user.isActive || !canBeAssignedJobs(user.role)) {
      return false;
    }
    
    // If property has no area, show all assignable users (fallback behavior)
    if (!property?.area) {
      return true;
    }
    
    // If user has no areas assigned, don't show them
    if (!user.areas || !Array.isArray(user.areas)) {
      return false;
    }
    
    // Show users who are assigned to the property's area
    return user.areas.includes(property.area);
  });
};

/**
 * Get users that can be assigned to jobs, filtered by property area AND service type allowed roles
 * @param {Array} users - Array of user objects
 * @param {Object} property - Property object with area information
 * @param {Object} serviceType - Service type object with allowedRoles array
 * @returns {Array} Filtered array of users
 */
export const getAssignableUsersForServiceType = (users, property = null, serviceType = null) => {
  return users.filter(user => {
    // Must be active
    if (!user.isActive) {
      return false;
    }
    
    // Area-based filtering (existing logic)
    if (property?.area) {
      // If user has no areas assigned, don't show them
      if (!user.areas || !Array.isArray(user.areas)) {
        return false;
      }
      
      // Show users who are assigned to the property's area
      if (!user.areas.includes(property.area)) {
        return false;
      }
    }
    
    // Service type role filtering (PRIORITY - this overrides general job assignability)
    if (serviceType?.allowedRoles && Array.isArray(serviceType.allowedRoles) && serviceType.allowedRoles.length > 0) {
      return serviceType.allowedRoles.includes(user.role);
    }
    
    // Fallback to general job assignability if no service type role restrictions
    return canBeAssignedJobs(user.role, user.areas);
  });
};

/**
 * Get users that can be assigned to work orders
 */
export const getWorkOrderAssignableUsers = (users) => {
  return users.filter(user => 
    user.isActive && canBeAssignedWorkOrders(user.role, user.areas)
  );
}; 